import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, MoveUpRight } from "lucide-react";
import {
  fetchSlideHtml,
  prefetchSlideHtmlAssets,
  withBaseHref,
} from "@/features/tutor/utils/slideHtmlLoader";
import {
  isAssessmentSlideType,
  isPracticeSlideType,
} from "@/features/tutor/constants/assessmentTypes";
import {
  PreviewFetchStatus,
  practicePreviewState,
  rowPreviewState,
} from "./lessonRows";
import AssessmentPreviewSurvey from "./AssessmentPreviewSurvey";
import PracticeDiagramFrame from "@/components/practiceDiagram/PracticeDiagramFrame";
import { usePracticeDiagram } from "@/components/practiceDiagram/practiceDiagramLoader";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "@/constants/slideDeck";
import { useFitScale, useMeasured } from "@/hooks/useFitScale";

const SURVEY_WIDTH = 520;
const SURVEY_HEIGHT = 560;

const AssessmentBoltIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="17"
    fill="currentColor"
    viewBox="0 0 256 256"
    aria-hidden="true"
  >
    <path d="M213.85,125.46l-112,120a8,8,0,0,1-13.69-7l14.66-73.33L45.19,143.49a8,8,0,0,1-3-13l112-120a8,8,0,0,1,13.69,7L153.18,90.9l57.63,21.61a8,8,0,0,1,3,12.95Z" />
  </svg>
);

const SlideItem = ({
  index,
  displayNumber,
  slideName,
  slideType,
  surveyJson,
  lessonId,
  slideHtmlUrl,
  practiceHostHtmlUrl,
  sourceStatus = "loaded",
  onRetry,
  onPreview,
}: {
  index: number;
  displayNumber?: number;
  slideName: string;
  slideType?: string | null;
  surveyJson?: Record<string, unknown> | null;
  lessonId: number;
  slideHtmlUrl?: string | null;
  practiceHostHtmlUrl?: string | null;
  sourceStatus?: PreviewFetchStatus;
  onRetry?: () => void;
  onPreview?: (index: number) => void;
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState<string | undefined>(undefined);
  const [placement, setPlacement] = useState<"below" | "above">("below");
  const [surveyHeight, setSurveyHeight] = useState<number | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const surveyRef = useRef<HTMLDivElement>(null);

  const isAssessment = isAssessmentSlideType(slideType);
  const isPractice = isPracticeSlideType(slideType);
  // Eager like the slide HTML below, so the hover preview is instant.
  const [practiceDiagram, retryPracticeDiagram] = usePracticeDiagram(
    practiceHostHtmlUrl,
    isPractice && sourceStatus === "loaded",
  );
  const stageWidth = isAssessment ? SURVEY_WIDTH : SLIDE_WIDTH;
  const stageHeight = isAssessment
    ? Math.min(surveyHeight ?? SURVEY_HEIGHT, SURVEY_HEIGHT)
    : SLIDE_HEIGHT;

  // Estimated popover height (scaled stage + label + padding) used to decide
  // whether it fits below the eye before it would clip the viewport bottom.
  const estimatedHeight = stageHeight * (240 / stageWidth) + 60;

  const handlePreview = (index: number) => {
    navigate(`/vo-demo/${lessonId}?slideIndex=${index}`);
  };

  const label = slideName || `Slide ${displayNumber ?? index + 1}`;

  // Eagerly prefetch the slide HTML on mount so the hover preview is instant.
  useEffect(() => {
    // A practice previews its host diagram instead (usePracticeDiagram).
    if (isPractice || !slideHtmlUrl || html !== undefined) return;
    const controller = new AbortController();
    fetchSlideHtml(slideHtmlUrl, controller.signal)
      .then((raw) => {
        prefetchSlideHtmlAssets(raw, slideHtmlUrl);
        setHtml(withBaseHref(raw, slideHtmlUrl));
      })
      .catch(() => {
        // Best-effort; spinner stays until a retry succeeds.
      });
    return () => controller.abort();
  }, [isPractice, slideHtmlUrl, html]);

  // Flip the popover above the eye when it would clip the viewport bottom.
  useLayoutEffect(() => {
    if (!open) return;
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setPlacement(spaceBelow < estimatedHeight ? "above" : "below");
  }, [open, estimatedHeight]);

  // Scale the fixed-size stage down to the popover width.
  const scale = useFitScale(stageRef, { width: stageWidth }, open, 0.42);

  useMeasured(
    surveyRef,
    open && isAssessment,
    (el) => el.offsetHeight,
    setSurveyHeight,
    [surveyJson],
  );

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const hasPreviewContent = isAssessment ? !!surveyJson : html !== undefined;
  const isFetchingHtml = !isAssessment && !!slideHtmlUrl && html === undefined;
  const previewState = isPractice
    ? practicePreviewState(sourceStatus, practiceDiagram.status)
    : rowPreviewState(
        hasPreviewContent,
        isFetchingHtml ? "loading" : sourceStatus,
      );
  // The slide list failing is retried by the card; the diagram, by this row.
  const handleRetry =
    isPractice && practiceDiagram.status === "error"
      ? retryPracticeDiagram
      : onRetry;

  return (
    <div
      key={index}
      className="flex flex-row items-center gap-4 py-4 text-[#294B39]"
    >
      {/* Every row leads with the same 30px mark, so the labels line up: the
          bolt for an assessment, the bite's number otherwise. */}
      <span className="flex flex-row items-center gap-3 whitespace-nowrap">
        {isAssessment ? (
          <span className="shrink-0 flex h-[30px] w-[30px] items-center justify-center rounded-[5px] border border-[#DED8CC] bg-[#EDE7D8] text-[#92733D]">
            <AssessmentBoltIcon />
          </span>
        ) : (
          <span className="shrink-0 flex h-[30px] w-[30px] items-center justify-center rounded-[5px] border border-[#DED8CC] bg-[#FBF8F1] text-[13px] font-bold tabular-nums text-[#28543D]">
            {displayNumber ?? index + 1}
          </span>
        )}
        {label}
      </span>
      <span className="flex-1 border-b border-dotted border-[#D9D9D9]" />
      <div
        ref={wrapperRef}
        className="relative shrink-0"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          aria-label={`Preview ${label} content`}
          className="text-[#28543D] hover:opacity-80 transition-opacity"
          onClick={() => {
            setOpen(false);
            onPreview?.(index);
          }}
        >
          <Eye size={20} />
        </button>

        {open && (
          <div
            className={`absolute right-0 z-50 w-[240px] max-w-[80vw] ${
              placement === "above" ? "bottom-full mb-3" : "top-full mt-3"
            }`}
          >
            <span
              className={`absolute right-2 h-3 w-3 rotate-45 rounded-sm bg-[#FBF8F1] ${
                placement === "above"
                  ? "-bottom-1.5 shadow-[2px_2px_5px_rgba(0,0,0,0.04)]"
                  : "-top-1.5 shadow-[-2px_-2px_5px_rgba(0,0,0,0.04)]"
              }`}
            />
            <div className="relative rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] p-2.5 shadow-[0px_8px_30px_rgba(0,0,0,0.12)]">
              <span className="mb-2 block font-mulish font-bold text-[#28543D] text-xs">
                {label}
              </span>
              <div
                ref={stageRef}
                className="relative w-full overflow-hidden rounded-[6px] border border-[#DED8CC] bg-white"
                style={{ height: stageHeight * scale }}
              >
                {previewState === "loading" && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#DED8CC] border-t-[#28543D]" />
                  </div>
                )}
                {isAssessment && surveyJson && (
                  <div
                    ref={surveyRef}
                    className="pointer-events-none"
                    style={{
                      width: stageWidth,
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                    }}
                  >
                    <AssessmentPreviewSurvey surveyJson={surveyJson} />
                  </div>
                )}
                {isPractice && practiceDiagram.status === "ready" && (
                  <PracticeDiagramFrame
                    diagram={practiceDiagram.diagram}
                    scale={scale}
                    title={label}
                  />
                )}
                {!isAssessment && !isPractice && html !== undefined && (
                  <iframe
                    srcDoc={html}
                    title={label}
                    className="border-0"
                    scrolling="no"
                    sandbox="allow-scripts allow-same-origin"
                    style={{
                      width: stageWidth,
                      height: stageHeight,
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                    }}
                  />
                )}
                {previewState === "empty" && (
                  <div className="absolute inset-0 flex items-center justify-center px-3 text-center text-xs text-[#687368]">
                    {isPractice
                      ? "No diagram for this practice."
                      : "Nothing to preview yet."}
                  </div>
                )}
                {previewState === "error" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-3 text-center">
                    <span className="text-xs text-[#B4453C]">
                      Couldn&rsquo;t load this preview.
                    </span>
                    {handleRetry && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRetry();
                        }}
                        className="text-xs font-bold text-[#28543D] underline hover:opacity-80"
                      >
                        Try again
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <span className="shrink-0 h-5 w-px bg-[#DED8CC]" aria-hidden="true" />
      <button
        type="button"
        aria-label={`Preview ${label}`}
        className="shrink-0 flex flex-row items-center gap-2 text-[#28543D] hover:opacity-80 transition-opacity"
        onClick={() => handlePreview(index)}
      >
        <span className="font-medium">Preview</span>
        <MoveUpRight size={18} />
      </button>
    </div>
  );
};

export default SlideItem;
